import { Input } from '@ibsys/kit';
import { type FC, type KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFilterValuesList } from './NumericFilterValuesList';
import {
  appendNumericFilterItems,
  formatNumericFilterItem,
  type NumericFilterItem,
  type NumericValueLimits,
  parseNumericFilterItems,
} from './numericFilterValue';

/** Пропсы отбора перечнем */
type Props = {
  /** Элементы перечня, с которыми открылось окно отбора */
  initialItems: NumericFilterItem[];
  limits: NumericValueLimits;
  onChange: (items: NumericFilterItem[]) => void;
  /** Перечень не набирается: отбор задан не значением */
  disabled?: boolean;
};

/**
 * Отбор перечнем: элементы перечисляются в одной строке через запятую и добавляются к набранным
 * разом. Элементом может быть и конкретное значение, и промежуток; то, что значением колонки быть
 * не может, в перечень не принимается — непринятое названо подсказкой и остаётся в поле, чтобы
 * править приходилось только его.
 */
export const NumericFilterValuesMode: FC<Props> = ({ initialItems, limits, onChange, disabled }) => {
  const { t } = useTranslation('common');
  const [items, setItems] = useState(initialItems);
  const [text, setText] = useState('');
  const [rejected, setRejected] = useState<string[]>([]);

  /**
   * Отбором считаются набранные элементы вместе с тем, что уже перечислено в поле. Иначе первое
   * нажатие «Применить» уходило бы на набор перечисленного: набранные элементы подрастили бы окно
   * и увели кнопку из-под курсора, так что отбор пришлось бы применять вторым нажатием.
   */
  const publish = (nextItems: NumericFilterItem[], nextText: string) =>
    onChange(appendNumericFilterItems(nextItems, parseNumericFilterItems(nextText, limits).items));

  const handleChange = (value: string) => {
    setText(value);
    setRejected([]);
    publish(items, value);
  };

  /** Перечисленное переходит в набранные: пригодное — элементами, непринятое остаётся в поле */
  const handleSubmit = () => {
    if (!text.trim()) return;
    const parsed = parseNumericFilterItems(text, limits);
    const next = appendNumericFilterItems(items, parsed.items);
    setItems(next);
    setRejected(parsed.rejected);
    setText(parsed.rejected.join(', '));
    publish(next, '');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    // Перечисленное добавляется к набранным, а не применяет отбор
    event.preventDefault();
    handleSubmit();
  };

  const handleRemove = (key: string) => {
    const next = items.filter((item) => formatNumericFilterItem(item) !== key);
    setItems(next);
    publish(next, text);
  };

  return (
    <div className="flex flex-col gap-2">
      <Input
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        // Непринятое называется как набрано, поэтому в подсказку идёт без экранирования
        error={
          rejected.length
            ? t('numericFilter.rejected', {
                items: rejected.join(', '),
                interpolation: { escapeValue: false },
              })
            : undefined
        }
        placeholder={t('numericFilter.valuesPlaceholder')}
        disabled={disabled}
      />

      <NumericFilterValuesList items={items} onRemove={handleRemove} disabled={disabled} />
    </div>
  );
};
